/**
 * foodNoise.service.ts
 *
 * Food Noise Signal — data model and persistence layer.
 *
 * One optional record per user per day.
 * Stored in standalone `foodNoise` Firestore collection.
 * Key: userId + date (YYYY-MM-DD).
 *
 * Design decisions (locked):
 * - Low-weight modifier signal (weight: 0.25). Never overrides nutrition logic.
 * - GLP-1 users only. Gating is enforced at component level, not here.
 * - No offline queue. Silent no-op if network unavailable.
 * - Missing days remain missing. No inference, no defaults.
 */

import { where, limit } from 'firebase/firestore';
import {
  addDocument,
  updateDocument,
  getCollection,
} from './firebase/firestore.service';
import { getTodayDate } from '../utils/date';
import dayjs from 'dayjs';

// ─── Constants ────────────────────────────────────────────────────────────────

const FOOD_NOISE_COLLECTION = 'foodNoise';

// Signal weight relative to other guidance signals.
// Used by guidance engine when combining signals.
// Protein: 1.0 | Calories: 1.0 | Hydration: 0.8 | Logging: 0.6 | Consistency: 0.4
export const FOOD_NOISE_SIGNAL_WEIGHT = 0.25;

// ─── Types ────────────────────────────────────────────────────────────────────

export type FoodNoiseLevel = 'quiet' | 'manageable' | 'loud';

export interface FoodNoiseEntry {
  id: string;
  userId: string;
  date: string;           // YYYY-MM-DD
  foodNoiseLevel: FoodNoiseLevel;
  createdAt: number;      // Unix ms
  updatedAt: number;      // Unix ms
}

// Shape written to Firestore (no id — auto-generated by addDocument)
type FoodNoisePayload = Omit<FoodNoiseEntry, 'id'>;

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Save or update today's food noise level.
 *
 * If no entry exists for today → create.
 * If entry already exists → overwrite foodNoiseLevel + updatedAt only.
 *
 * Returns the saved entry including its Firestore id.
 */
export async function saveFoodNoise(
  userId: string,
  level: FoodNoiseLevel,
): Promise<FoodNoiseEntry> {
  if (!(['quiet', 'manageable', 'loud'] as const).includes(level)) {
    throw new Error(`Invalid food noise level: "${level}". Must be quiet, manageable, or loud.`);
  }

  const date = getTodayDate();
  const now = Date.now();

  const existing = await getFoodNoiseForDate(userId, date);

  if (existing) {
    await updateDocument(`${FOOD_NOISE_COLLECTION}/${existing.id}`, {
      foodNoiseLevel: level,
      updatedAt: now,
    });
    return { ...existing, foodNoiseLevel: level, updatedAt: now };
  }

  const payload: FoodNoisePayload = {
    userId,
    date,
    foodNoiseLevel: level,
    createdAt: now,
    updatedAt: now,
  };

  const result = await addDocument<FoodNoisePayload>(FOOD_NOISE_COLLECTION, payload);
  return { ...payload, id: result.id };
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Get today's food noise entry for a user.
 * Returns null if not yet answered today.
 */
export async function getTodayFoodNoise(
  userId: string,
): Promise<FoodNoiseEntry | null> {
  return getFoodNoiseForDate(userId, getTodayDate());
}

/**
 * Get food noise entry for a specific date.
 * Returns null if no entry exists for that date.
 */
export async function getFoodNoiseForDate(
  userId: string,
  date: string,
): Promise<FoodNoiseEntry | null> {
  const results = await getCollection<FoodNoiseEntry>(
    FOOD_NOISE_COLLECTION,
    where('userId', '==', userId),
    where('date', '==', date),
    limit(1),
  );
  return results.length > 0 ? results[0] : null;
}

/**
 * Get food noise entries for the last N days (inclusive of today).
 * Returns only days where the user answered — missing days are absent.
 *
 * Used by the guidance layer for pattern detection.
 */
export async function getRecentFoodNoise(
  userId: string,
  days: number = 7,
): Promise<FoodNoiseEntry[]> {
  const today = dayjs();
  const dates: string[] = [];

  for (let i = 0; i < days; i++) {
    dates.push(today.subtract(i, 'day').format('YYYY-MM-DD'));
  }

  // Fetch each day individually — sparse data, no range query needed.
  // Missing days return null and are filtered out.
  const results = await Promise.all(
    dates.map(date => getFoodNoiseForDate(userId, date)),
  );

  return results.filter((entry): entry is FoodNoiseEntry => entry !== null);
}

/**
 * Get food noise entries for last 3 days.
 * Convenience wrapper used by guidance pattern checks.
 */
export async function getFoodNoiseLast3Days(
  userId: string,
): Promise<FoodNoiseEntry[]> {
  return getRecentFoodNoise(userId, 3);
}

/**
 * Get food noise entries for last 7 days.
 * Convenience wrapper for weekly coaching inputs.
 */
export async function getFoodNoiseLast7Days(
  userId: string,
): Promise<FoodNoiseEntry[]> {
  return getRecentFoodNoise(userId, 7);
}
